import React, { useMemo } from 'react';
import { ApplicableRole } from '../api/access';

interface Props {
    /** 可申请角色 (GET /v1/access/roles), 含预置 <资源>-viewer / -admin 与自定义角色. */
    roles: ApplicableRole[];
    /** 已勾选的角色名. */
    picked: string[];
    onChange: (picked: string[]) => void;
    /** 已持有的角色名 (追加申请时): 已有管理员 → 该资源整行不再出现; 已有只读 → 只能再申请管理. */
    held?: string[];
    /** 传入则在顶部固定展示「普通用户 · 默认包含」(首次申请页); 追加申请时不传. */
    userRole?: ApplicableRole | null;
    loading?: boolean;
    emptyHint?: string;
}

type Tier = 'none' | 'viewer' | 'admin';

interface ResourceRow {
    resource: string;
    label: string;
    group: string;
    sensitive: boolean;
    viewer?: ApplicableRole;
    admin?: ApplicableRole;
}

/**
 * 角色勾选矩阵 (2026-09-12): 预置资源角色按「资源 × 只读/管理」呈现, 一行一个资源, 三档单选
 * (不申请 / 只读 / 管理; 管理已含只读, 不会同时申请两个); 自定义角色列在末尾按普通勾选项.
 * 首次申请页 (AccessGate) 与顶栏「申请权限」弹窗 (RoleApplyModal) 共用.
 */
const RolePickMatrix = ({
    roles,
    picked,
    onChange,
    held = [],
    userRole,
    loading,
    emptyHint,
}: Props) => {
    const { groups, modules, custom } = useMemo(() => {
        const byRes = new Map<string, ResourceRow>();
        const custom: ApplicableRole[] = [];
        const modules: ApplicableRole[] = [];
        for (const r of roles) {
            if (r.name === 'user') continue;
            if (r.tier === 'module') {
                modules.push(r);
                continue;
            }
            if (!r.resource || !r.tier) {
                custom.push(r);
                continue;
            }
            let row = byRes.get(r.resource);
            if (!row) {
                row = {
                    resource: r.resource,
                    label: r.resourceLabel || r.resource,
                    group: r.group || '其他',
                    sensitive: !!r.sensitive,
                };
                byRes.set(r.resource, row);
            }
            row[r.tier] = r;
        }
        // 已是该资源管理员的整行不出现 (没有更高档可申请)
        const rows = [...byRes.values()].filter(
            (row) => !(row.admin && held.includes(row.admin.name)),
        );
        const groups: { name: string; rows: ResourceRow[] }[] = [];
        for (const row of rows) {
            const g = groups.find((x) => x.name === row.group);
            if (g) g.rows.push(row);
            else groups.push({ name: row.group, rows: [row] });
        }
        return {
            groups,
            modules: modules.filter((r) => !held.includes(r.name)),
            custom: custom.filter((r) => !held.includes(r.name)),
        };
    }, [roles, held]);

    const tierOf = (row: ResourceRow): Tier => {
        if (row.admin && picked.includes(row.admin.name)) return 'admin';
        if (row.viewer && picked.includes(row.viewer.name)) return 'viewer';
        return 'none';
    };
    const setTier = (row: ResourceRow, tier: Tier) => {
        const drop = [row.viewer?.name, row.admin?.name].filter(
            Boolean,
        ) as string[];
        const next = picked.filter((n) => !drop.includes(n));
        if (tier === 'viewer' && row.viewer) next.push(row.viewer.name);
        if (tier === 'admin' && row.admin) next.push(row.admin.name);
        onChange(next);
    };
    const toggleCustom = (name: string) =>
        onChange(
            picked.includes(name)
                ? picked.filter((x) => x !== name)
                : [...picked, name],
        );

    const total =
        groups.reduce((n, g) => n + g.rows.length, 0) +
        custom.length +
        modules.length;
    // 只读一律按模块 (2026-09-12 定稿): 没有任何 <资源>-viewer 时精细区只剩「管理」一档, 隐藏只读列
    const hasViewerTier = groups.some((g) => g.rows.some((r) => r.viewer));
    return (
        <div className="rounded-lg border border-slate-200 max-h-[50vh] overflow-y-auto">
            {userRole !== undefined && (
                <label className="flex items-start gap-3 px-4 py-3 bg-slate-50/60 border-b border-slate-100">
                    <input type="checkbox" checked disabled className="mt-1" />
                    <div>
                        <div className="text-sm font-medium text-slate-800">
                            {userRole?.displayName || '普通用户'}
                            <span className="ml-2 text-xs text-slate-400">
                                默认包含
                            </span>
                        </div>
                        <div className="text-xs text-slate-500">
                            {userRole?.description ||
                                '只看自己提交的工单、管理自己的 OpenAPI 令牌;业务数据需在下方申请'}
                        </div>
                    </div>
                </label>
            )}
            {modules.length > 0 && (
                <div className="border-b border-slate-100">
                    <div className="px-4 py-1.5 text-xs font-medium text-slate-500 bg-slate-50/70">
                        按模块申请 (推荐) · 一个菜单大类一个只读角色;
                        基础数据另有管理员
                    </div>
                    <div className="grid grid-cols-2 divide-y divide-slate-100">
                        {modules.map((r) => (
                            <label
                                key={r.name}
                                className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={picked.includes(r.name)}
                                    onChange={() => toggleCustom(r.name)}
                                />
                                <div>
                                    <div className="text-sm text-slate-800">
                                        {r.displayName}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {r.description}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            )}
            {groups.length > 0 && (
                <details className="group">
                    <summary className="px-4 py-1.5 text-xs font-medium text-slate-500 bg-slate-50/70 cursor-pointer select-none">
                        {hasViewerTier
                            ? '按单个资源精细申请 (只读 / 管理) ▸'
                            : '按单个资源申请管理员 (查看 + 新建 / 编辑 / 删除 / 发布) ▸'}
                    </summary>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0">
                            <tr>
                                <th className="text-left font-medium px-4 py-2">
                                    资源
                                </th>
                                <th className="w-24 text-center font-medium px-2 py-2">
                                    不申请
                                </th>
                                <th
                                    className="w-24 text-center font-medium px-2 py-2"
                                    title="查看该资源已发布的数据"
                                    hidden={!hasViewerTier}
                                >
                                    只读
                                </th>
                                <th
                                    className="w-24 text-center font-medium px-2 py-2"
                                    title="查看 + 新建 / 编辑 / 删除 / 发布 (写操作仍走审批)"
                                >
                                    管理
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((g) => (
                                <React.Fragment key={g.name}>
                                    <tr className="bg-slate-50/70">
                                        <td
                                            colSpan={4}
                                            className="px-4 py-1.5 text-xs font-medium text-slate-500"
                                        >
                                            {g.name}
                                        </td>
                                    </tr>
                                    {g.rows.map((row) => {
                                        const cur = tierOf(row);
                                        // 已持有只读 → 只读档锁定为"已有", 只能再申请管理
                                        const hasViewer =
                                            !!row.viewer &&
                                            held.includes(row.viewer.name);
                                        const name = `tier-${row.resource}`;
                                        return (
                                            <tr
                                                key={row.resource}
                                                className="border-t border-slate-100 hover:bg-slate-50"
                                            >
                                                <td className="px-4 py-2">
                                                    <span className="text-slate-800">
                                                        {row.label}
                                                    </span>
                                                    {row.sensitive && (
                                                        <span className="ml-2 tag bg-rose-50 text-rose-600">
                                                            敏感
                                                        </span>
                                                    )}
                                                    <span className="ml-2 font-mono text-[11px] text-slate-400">
                                                        {row.resource}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <input
                                                        type="radio"
                                                        name={name}
                                                        checked={cur === 'none'}
                                                        onChange={() =>
                                                            setTier(row, 'none')
                                                        }
                                                    />
                                                </td>
                                                <td
                                                    className="text-center"
                                                    hidden={!hasViewerTier}
                                                >
                                                    {hasViewer ? (
                                                        <span
                                                            className="text-xs text-slate-400"
                                                            title="已持有"
                                                        >
                                                            已有
                                                        </span>
                                                    ) : row.viewer ? (
                                                        <input
                                                            type="radio"
                                                            name={name}
                                                            checked={
                                                                cur === 'viewer'
                                                            }
                                                            onChange={() =>
                                                                setTier(
                                                                    row,
                                                                    'viewer',
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <span
                                                            className="text-xs text-slate-300"
                                                            title="该资源不设只读角色"
                                                        >
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <input
                                                        type="radio"
                                                        name={name}
                                                        disabled={!row.admin}
                                                        checked={
                                                            cur === 'admin'
                                                        }
                                                        onChange={() =>
                                                            setTier(
                                                                row,
                                                                'admin',
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}
            {custom.length > 0 && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                    <div className="px-4 py-1.5 text-xs font-medium text-slate-500 bg-slate-50/70">
                        其他角色
                    </div>
                    {custom.map((r) => (
                        <label
                            key={r.name}
                            className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={picked.includes(r.name)}
                                onChange={() => toggleCustom(r.name)}
                            />
                            <div>
                                <div className="text-sm text-slate-800">
                                    {r.displayName}
                                    <span className="ml-2 font-mono text-xs text-slate-400">
                                        {r.name}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500">
                                    {r.description}
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
            )}
            {loading && (
                <div className="px-4 py-3 text-xs text-slate-400">
                    角色清单加载中…
                </div>
            )}
            {!loading && total === 0 && emptyHint && (
                <div className="px-4 py-3 text-xs text-slate-400">
                    {emptyHint}
                </div>
            )}
        </div>
    );
};

export default RolePickMatrix;
